import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { Stage, Task } from "../../api/types";
import { CI_META, GATE_COLOR, GATE_LABEL } from "../../lib/status";
import { cx } from "../../lib/format";
import { Icon } from "../Icon";
import { Pill, StatusPill } from "../Pill";

export function TaskCardBody({ task, stage }: { task: Task; stage: Stage | undefined }) {
  const gate = task.gate_index !== null && stage ? stage.gates[task.gate_index] : undefined;
  // "Done" is already implied by the column; other statuses (incl. cancelled) still say something.
  const showStatus = task.status !== "done";
  return (
    <>
      <div className="card-title">
        <span className="card-number">#{task.number}</span> {task.title || <em className="muted">Untitled</em>}
      </div>
      {(showStatus || gate) && (
        <div className="card-props">
          {showStatus && <StatusPill status={task.status} />}
          {gate && (
            <Pill color={GATE_COLOR[gate.type]} title={`Gate ${task.gate_index! + 1}: ${GATE_LABEL[gate.type]}`}>
              <Icon name="gate" size={12} /> {gate.name || GATE_LABEL[gate.type]}
            </Pill>
          )}
        </div>
      )}
      {(task.pr_number !== null || task.ci_status) && (
        <div className="card-meta">
          {task.pr_number !== null && (
            <a
              className="card-meta-item card-pr"
              href={task.pr_url ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <Icon name="pr" size={13} /> #{task.pr_number}
            </a>
          )}
          {task.ci_status && <Pill color={CI_META[task.ci_status].color}>{CI_META[task.ci_status].label}</Pill>}
        </div>
      )}
      {task.attention_reason && (
        <div className="card-attention">
          <Icon name="alert" size={13} /> <span>{task.attention_reason}</span>
        </div>
      )}
    </>
  );
}

export function SortableTaskCard({
  task,
  stage,
  selected,
  onOpen,
}: {
  task: Task;
  stage: Stage | undefined;
  selected: boolean;
  onOpen: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    data: { type: "task", stageKey: task.stage_key },
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cx("card", isDragging && "card-dragging", selected && "card-selected")}
      {...attributes}
      {...listeners}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen();
        else listeners?.onKeyDown?.(e);
      }}
      aria-label={`#${task.number} ${task.title}`}
    >
      <TaskCardBody task={task} stage={stage} />
    </div>
  );
}
